import type { Dispatch, SetStateAction } from "react";
import PayeeField from "../../components/PayeeField";
import type { ExpenseInput, Payee } from "../../types";

export interface EntryTitlePayeeFieldsProps {
  entryType: "Expense" | "Income" | "Transfer";
  isIOU: boolean;
  input: ExpenseInput;
  setInput: Dispatch<SetStateAction<ExpenseInput>>;
  field: (key: keyof ExpenseInput, value: string) => void;
  payees: Payee[];
  tripTitles: string[];
}

export default function EntryTitlePayeeFields({
  entryType,
  isIOU,
  input,
  setInput,
  field,
  payees,
  tripTitles,
}: EntryTitlePayeeFieldsProps) {
  if (entryType === "Transfer") return null;
  const draftMode = !isIOU && input.draftPayeeName !== undefined;

  return (
    <>
      <label>
        {entryType === "Income" ? "Source / description" : "What / title"}
        <input
          name="title"
          autoComplete="off"
          list={input.tripId && tripTitles.length > 0 ? "trip-title-list" : undefined}
          value={input.title}
          onChange={(e) => field("title", e.target.value)}
          placeholder={entryType === "Income" ? "e.g. Salary from employer" : "e.g. Mom's birthday cake, 3 party pizzas"}
          maxLength={160}
          // A loan entry identifies itself by payee, so this stays optional there rather than
          // blocking the save on a field that never reaches the Sheet for that path.
          required={!isIOU}
        />
        {input.tripId && tripTitles.length > 0 && (
          <datalist id="trip-title-list">
            {tripTitles.map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
        )}
      </label>
      <label>
        <span className="field-label">
          Payee
          <small>
            {!input.payeeId && input.draftPayeeName ? "Saved with transaction"
              : !input.payeeId && input.payeeName ? "New — created when saved" : "Optional"}
          </small>
        </span>
        <PayeeField
          payees={payees}
          ariaLabel="Payee"
          deferCreate={!draftMode}
          value={input.payeeId || ""}
          pendingName={!input.payeeId || input.payeeId.startsWith("local:") ? input.draftPayeeName || input.payeeName : undefined}
          onDraftChange={draftMode ? (draftPayeeName) => setInput(v => ({
            ...v, draftPayeeName, payeeId: "", payeeName: undefined,
          })) : undefined}
          onChange={(payeeId, payeeName) =>
            // Clearing drops both id and deferred name. A local:/offline create keeps the name
            // until sync resolves or creates the payee server-side.
            setInput((v) => ({
              ...v,
              payeeId: payeeId || "",
              payeeName: payeeId && !payeeId.startsWith("local:") ? undefined : (payeeName || ""),
              ...(draftMode ? { draftPayeeName: "" } : {}),
            }))
          }
        />
      </label>
    </>
  );
}
